"use client";

import dictionary from "@/utils/dictionary";
import { EMAIL } from "@/assets/static/contact";
import { PROJECTS, SKILLS } from "@/assets/static/data";
import EnvelopeFilled from "@/assets/vectors/EnvelopeFilled";
import FlowChartFilled from "@/assets/vectors/FlowChartFilled";
import MagnifyingGlass from "@/assets/vectors/MagnifyingGlass";
import MusicNote from "@/assets/vectors/MusicNote";
import PersonFilled from "@/assets/vectors/PersonFilled";
import TrayFilled from "@/assets/vectors/TrayFilled";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { haptics } from "@/utils/haptics";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./command_palette.module.scss";

interface CommandItem {
    id: string;
    title: string;
    subtitle?: string;
    category: string;
    action: () => void;
    icon?: any;
}

export default function CommandPalette() {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const router = useRouter();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [isMac, setIsMac] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
        setMounted(true);
    }, []);

    // Keyboard shortcut to open (Cmd+K or Ctrl+K)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                setIsOpen((open) => !open);
            }
            if (e.key === "Escape" && isOpen) {
                haptics.click();
                setIsOpen(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            haptics.unlock();
            haptics.click();
            setQuery("");
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const navigateTo = (href: string) => {
        setIsOpen(false);
        if (href.startsWith("mailto:")) {
            window.location.href = href;
        } else if (href.startsWith("http")) {
            window.open(href, "_blank");
        } else if (href.includes("#")) {
            // Simulate an anchor click to trigger BodyWrapper's shine and scroll effects
            const a = document.createElement("a");
            a.href = href;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            router.push(href);
        }
    };

    const items: CommandItem[] = useMemo(() => {
        const baseItems: CommandItem[] = [
            { id: "nav-daily-brief", title: dictionary.brief.name, subtitle: dictionary.brief.description, category: "Navigation", action: () => navigateTo("/daily-developer-brief"), icon: TrayFilled },
            { id: "nav-home", title: "Go to Home", category: "Navigation", action: () => navigateTo("/"), icon: PersonFilled },
            { id: "nav-resume", title: "Download Resume", subtitle: "Print-ready PDF view", category: "Actions", action: () => navigateTo("/resume"), icon: TrayFilled },
            { id: "action-music", title: "Play Music", subtitle: "Listen to ambient tracks", category: "Actions", action: () => navigateTo("/music"), icon: MusicNote },
            { id: "action-email", title: "Send Email", subtitle: EMAIL, category: "Actions", action: () => navigateTo(`mailto:${EMAIL}`), icon: EnvelopeFilled },
            { id: "nav-about", title: "About Section", category: "Navigation", action: () => navigateTo("/#section_about"), icon: PersonFilled },
            { id: "nav-projects", title: "Projects Section", category: "Navigation", action: () => navigateTo("/#section_projects"), icon: FlowChartFilled },
            { id: "nav-skills", title: "Skills Section", category: "Navigation", action: () => navigateTo("/#section_skills"), icon: FlowChartFilled },
            { id: "nav-contact", title: "Contact Section", category: "Navigation", action: () => navigateTo("/#section_contact"), icon: EnvelopeFilled },
        ];

        const projectItems: CommandItem[] = PROJECTS.map((p) => ({
            id: `proj-${p.name}`,
            title: p.name,
            subtitle: p.description.replace(/<[^>]+>/g, '').substring(0, 60) + "...",
            category: "Case Studies",
            action: () => navigateTo(p.case_study ? `/case-study/${p.case_study.slug}` : `/#section_projects`),
            icon: FlowChartFilled,
        }));

        const skillItems: CommandItem[] = SKILLS.map((s) => ({
            id: `skill-${s.name}`,
            title: s.name,
            subtitle: s.slogan.replace(/<[^>]+>/g, ''),
            category: "Skills",
            action: () => navigateTo("/#section_skills"),
            icon: FlowChartFilled,
        }));

        return [...baseItems, ...projectItems, ...skillItems];
    }, [router]);

    const filteredItems = useMemo(() => {
        if (!query) return items;
        const lowerQuery = query.toLowerCase();
        return items.filter((item) =>
            item.title.toLowerCase().includes(lowerQuery) ||
            item.category.toLowerCase().includes(lowerQuery) ||
            item.subtitle?.toLowerCase().includes(lowerQuery)
        );
    }, [query, items]);

    // Handle arrow keys
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
                haptics.hover();
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
                haptics.hover();
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (filteredItems[selectedIndex]) {
                    haptics.click();
                    filteredItems[selectedIndex].action();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, filteredItems, selectedIndex]);

    // Scroll selected item into view
    useEffect(() => {
        if (isOpen && listRef.current) {
            const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
        }
    }, [selectedIndex, isOpen]);

    if (!mounted) return null;

    return (
        <>
            <button
                className={styles.trigger_pill}
                onClick={() => { haptics.unlock(); haptics.click(); setIsOpen(true); }}
                aria-label={`Open Command Palette (${isMac ? '⌘K' : 'Ctrl+K'})`}
            >
                <MagnifyingGlass className={styles.trigger_icon} />
                <span className={styles.trigger_shortcut}>{isMac ? '⌘K' : 'Ctrl+K'}</span>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className={styles.overlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => { haptics.click(); setIsOpen(false); }}
                    >
                        <motion.div
                            className={styles.palette}
                            initial={{ scale: 0.95, y: -20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: -20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.header}>
                                <input
                                    ref={inputRef}
                                    className={styles.input}
                                    placeholder="Search projects, skills, or actions..."
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        setSelectedIndex(0);
                                    }}
                                />
                                <div className={styles.badge}>ESC</div>
                            </div>

                            <ul ref={listRef} className={styles.list}>
                                {filteredItems.length === 0 ? (
                                    <li className={styles.empty}>No results found.</li>
                                ) : (
                                    filteredItems.map((item, idx) => (
                                        <li
                                            key={item.id}
                                            className={clsx(styles.item, selectedIndex === idx && styles.selected)}
                                            onMouseEnter={() => { setSelectedIndex(idx); haptics.hover(); }}
                                            onClick={() => { haptics.click(); item.action(); }}
                                        >
                                            {item.icon && <item.icon className={styles.icon} />}
                                            <div className={styles.content}>
                                                <span className={styles.title}>{item.title}</span>
                                                {item.subtitle && <span className={styles.subtitle}>{item.subtitle}</span>}
                                            </div>
                                            <span className={styles.category}>{item.category}</span>
                                        </li>
                                    ))
                                )}
                            </ul>

                            <div className={styles.footer}>
                                <div className={styles.hints}>
                                    <div className={styles.hint_key}>
                                        <span>↑↓</span> to navigate
                                    </div>
                                    <div className={styles.hint_key}>
                                        <span>↵</span> to select
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
